import React from "react";
import PropTypes from "prop-types";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import SectionCard from "../SectionCard";
import SectionSkeleton from "../SectionSkeleton";

/**
 * ViewersAlsoView — the shared "Viewers also view" rail card (chrome half; the data half is
 * useViewersAlsoView). A `<SectionCard>` with a stack of profile rows: round avatar + linked
 * name + optional subtitle. Model-agnostic — items are the plain display objects the engine's
 * hydrate adapter produced; this card knows nothing about which profile engine it's on.
 *
 * While loading it renders a section-shaped skeleton; when the item list is empty it renders
 * nothing. The page decides whether an empty resolve collapses the rail column to full-width
 * or leaves it for other rail cards (see the Company sidebar's rail-collapse note for the
 * reflow tradeoff).
 *
 * @param {Object} props
 * @param {Array} props.items - Display items: `{ id, modelType?, href, imageUrl, title, subtitle }`
 *   (`modelType` disambiguates colliding numeric ids on multi-model lists)
 * @param {boolean} [props.isLoading] - True while the similarity lookup + hydration are in flight
 * @param {string} [props.title] - Card title
 */
function ViewersAlsoView({ items, isLoading = false, title = "Viewers also view" }) {
  if (isLoading) return <SectionSkeleton lines={5} />;
  if (!items.length) return null;

  return (
    <SectionCard id="viewers-also-view" title={title}>
      <div className="d-flex flex-column gap-3">
        {items.map((item) => (
          // Numeric ids collide across model types on multi-model lists, so the key includes the type.
          <div key={`${item.modelType || ""}:${item.id}`} className="d-flex flex-row align-items-center">
            <ImageCropped
              roundedCircle
              wrapperClasses="me-2 flex-shrink-0"
              src={item.imageUrl}
              alt={item.title}
              size={45}
              fallbackToInitials
            />
            <div className="overflow-hidden">
              <div className="fw-bold text-truncate">
                <a href={item.href}>{item.title}</a>
              </div>
              {item.subtitle && <div className="text-muted small text-truncate">{item.subtitle}</div>}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

ViewersAlsoView.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      modelType: PropTypes.string,
      href: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
    }),
  ).isRequired,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
};

export default ViewersAlsoView;
